import { startTrace, stopTrace } from '../analytics';
import { type FirebaseStorage } from 'firebase/storage';

/**
 * Fetches a JSON collection from Firebase Storage.
 * Falls back to a provided function if the storage fetch fails or is not found.
 */
export async function fetchCollectionFromStorage<T>(
    storage: FirebaseStorage,
    storagePath: string,
    fallback: () => Promise<T[]>
): Promise<T[]> {
    const storageTrace = startTrace(`fetch_collection_${storagePath.replace(/\//g, '_')}`);
    try {
        // Since we made bulk-data public in storage.rules, we can use deterministic URLs 
        // to bypass the 1.2s getDownloadURL() handshake overhead.
        const bucket = 'bible-glen.firebasestorage.app';
        const encodedPath = encodeURIComponent(storagePath);
        const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Storage fetch failed: ${response.statusText}`);
        }

        const data = await response.json();
        stopTrace(storageTrace, { path: storagePath, count: data.length.toString() });
        return data as T[];
    } catch (err) {
        stopTrace(storageTrace, { status: 'fallback' });
        console.warn(`[Storage] Failed to fetch ${storagePath}, falling back to DB:`, err);
        return await fallback();
    }
}
/**
 * Fetches a JSON bundle from Firebase Storage using direct public URLs.
 */
export async function fetchBundleFromStorage<T extends Record<string, any[]>>(
    storagePath: string,
    fallback: () => Promise<T>
): Promise<T> {
    const bundleTrace = startTrace(`fetch_bundle_${storagePath.replace(/\//g, '_')}`);
    try {
        const bucket = 'bible-glen.firebasestorage.app';
        const encodedPath = encodeURIComponent(storagePath);
        const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Storage bundle fetch failed: ${response.statusText}`);
        }

        const data = await response.json();
        const totalItems = Object.values(data).reduce((sum: number, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);

        stopTrace(bundleTrace, { path: storagePath, total_items: totalItems.toString() });
        return data as T;
    } catch (err) {
        stopTrace(bundleTrace, { status: 'fallback' });
        console.warn(`[Storage] Failed to fetch bundle ${storagePath}, falling back:`, err);
        return await fallback();
    }
}
